import type { ErrorBoundaryProps } from '@sentry/react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type Props = Omit<ErrorBoundaryProps, 'fallback'>;

type ErrorFallbackProps = {
    error: unknown;
    eventId: string;
    resetError: () => void;
};

function ErrorFallback({ error, eventId, resetError }: ErrorFallbackProps) {
    const { t } = useTranslation();
    const errorMessage = error instanceof Error ? error.message : String(error);

    return (
        <div className="mx-auto flex w-full max-w-7xl flex-auto flex-col justify-center px-6 py-24 sm:py-64 lg:px-8">
            <p className="text-lg font-semibold leading-8 text-indigo-600">500</p>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
                {t('error_page__title')}
            </h1>
            <p className="mt-6 text-lg leading-7 text-neutral-600">
                {t('error_page__description', { eventId })}
            </p>
            <pre style={{ color: 'red' }}>{errorMessage}</pre>
            <div className="mt-10 flex items-center gap-4">
                <Link
                    to="/"
                    onClick={resetError}
                    className="text-base font-semibold leading-7 text-indigo-600"
                >
                    <span aria-hidden="true">&larr;</span> {t('error_page__try_again')}
                </Link>
            </div>
        </div>
    );
}

export function ErrorBoundary({ ...props }: Props) {
    return (
        <SentryErrorBoundary
            fallback={({ error, resetError, eventId }) => (
                <ErrorFallback error={error} eventId={eventId} resetError={resetError} />
            )}
            {...props}
        />
    );
}
